import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Check } from "lucide-react";
import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "../context/ThemeContext";

export default function ResetPassword() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!token) {
      setError("Missing reset token.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white relative grain">
      <Header />
      <div className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[400px] rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ backgroundColor: theme.color }}
        />
        <div
          className="relative w-full max-w-md p-8 bg-[#0A0A0A] border"
          style={{ borderColor: theme.color, boxShadow: `0 0 50px ${theme.color}33` }}
          data-testid="reset-password-card"
        >
          {!done ? (
            <>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
                <KeyRound className="h-3 w-3 theme-text" /> New password
              </div>
              <h1 className="font-display font-black text-3xl tracking-tight">Set a new password</h1>
              <p className="text-sm text-zinc-400 mt-1">Minimum 6 characters. Make it strong.</p>

              <form onSubmit={onSubmit} className="mt-8 space-y-4">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">New password</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    data-testid="reset-password"
                    className="w-full h-11 px-4 bg-black/60 border border-white/10 focus:border-[var(--theme-primary)] outline-none text-sm"
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Confirm password</span>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    data-testid="reset-confirm"
                    className="w-full h-11 px-4 bg-black/60 border border-white/10 focus:border-[var(--theme-primary)] outline-none text-sm"
                  />
                </label>

                {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}

                <button
                  type="submit"
                  disabled={loading || !token}
                  className="w-full inline-flex items-center justify-center gap-2 h-12 font-display font-bold tracking-wide disabled:opacity-60"
                  style={{ backgroundColor: theme.color, color: "#000", boxShadow: `0 0 28px ${theme.color}77` }}
                  data-testid="reset-submit"
                >
                  {loading ? "UPDATING…" : "UPDATE PASSWORD"}
                </button>
                {!token && (
                  <p className="text-xs text-red-400 text-center">
                    Missing token — open the reset link from your email.
                  </p>
                )}
              </form>
            </>
          ) : (
            <div className="text-center py-4 space-y-5" data-testid="reset-success">
              <div
                className="mx-auto h-14 w-14 rounded-full flex items-center justify-center pulse-glow"
                style={{ backgroundColor: theme.color }}
              >
                <Check className="h-7 w-7 text-black" strokeWidth={3} />
              </div>
              <h3 className="font-display font-black text-xl">Password updated</h3>
              <p className="text-sm text-zinc-400">Redirecting to login…</p>
              <Link to="/login" className="text-xs theme-text hover:underline">Go now</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
